import React from 'react';
import { Type, Lightbulb } from 'lucide-react';

/**
 * Provides actionable design suggestions by analyzing slide text and fonts
 * Shows specific text elements that need changes
 */
export function ActionableDesignSuggestions({ slide, designQuality, expertiseLevel = 'beginner' }) {
  if (!designQuality || !slide) return null;

  const suggestions = [];

  // Check slide type - different slides have different design requirements
  const hasTable = slide.tables > 0 || slide.slide_characteristics?.has_table;
  const hasChart = slide.charts > 0 || slide.slide_characteristics?.has_chart;
  const isDataSlide = hasTable || hasChart;
  const isMinimal = slide.slide_characteristics?.is_minimal_text || false;
  const isNavigation = slide.slide_characteristics?.is_navigation || false;

  // Analyze typography issues (skip for special slide types)
  const typographyScore = designQuality.scores?.typography_hierarchy || 0;
  const shouldSkipTypographySuggestions = isDataSlide || isMinimal || isNavigation;

  if (typographyScore < 50 && !shouldSkipTypographySuggestions) {
    // Extract text from slide - ensure it's an array
    const textBoxes = Array.isArray(slide.text_boxes) ? slide.text_boxes : [];
    const fonts = Array.isArray(slide.fonts) ? slide.fonts : [];

    // Only proceed if we have text boxes
    if (textBoxes.length === 0) {
      suggestions.push({
        type: 'typography',
        icon: Type,
        title: expertiseLevel === 'expert' ? 'Add Text Hierarchy' : 'Use Different Text Sizes',
        description: expertiseLevel === 'expert'
          ? 'Use at least 3 distinct text sizes: H1 for main title, H2 for subtitles, Body for content.'
          : 'Use 3 different text sizes: big titles, medium subtitles, and normal text.',
        actionable: expertiseLevel === 'expert'
          ? 'Suggested: H1 (44pt), H2 (24pt), Body (16pt)'
          : 'Try: 44pt for titles, 24pt for subtitles, 16pt for normal text',
        color: 'blue'
      });
    } else {
      // Get unique font sizes
      const fontSizes = new Set();
      textBoxes.forEach(box => {
        if (box.font_size) fontSizes.add(box.font_size);
      });

      const uniqueSizesCount = fontSizes.size;

      if (uniqueSizesCount < 3) {
        // Get the largest text (likely title)
        const largestText = textBoxes.reduce((largest, box) => {
          if (!box.text || !box.font_size) return largest;
          if (!largest || box.font_size > largest.font_size) return box;
          return largest;
        }, null);

        if (largestText && largestText.text) {
          const textPreview = largestText.text.substring(0, 50) + (largestText.text.length > 50 ? '...' : '');

          suggestions.push({
            type: 'typography',
            icon: Type,
            title: expertiseLevel === 'expert' ? 'Improve Text Hierarchy' : 'Make Text Sizes More Different',
            description: expertiseLevel === 'expert'
              ? `Create clearer size contrast. Your heading "${textPreview}" should be significantly larger than body text.`
              : `Make your title "${textPreview}" much bigger than the rest of the text so it stands out more.`,
            actionable: expertiseLevel === 'expert'
              ? `Suggested sizes: Title 44-54pt, Subtitle 24-28pt, Body 14-18pt`
              : `Try: Big title (44pt), Medium subtitle (24pt), Small text (16pt)`,
            color: 'blue'
          });
        } else {
          suggestions.push({
            type: 'typography',
            icon: Type,
            title: expertiseLevel === 'expert' ? 'Add Text Hierarchy' : 'Use Different Text Sizes',
            description: expertiseLevel === 'expert'
              ? 'Use at least 3 distinct text sizes: H1 for main title, H2 for subtitles, Body for content.'
              : 'Use 3 different text sizes: big titles, medium subtitles, and normal text.',
            actionable: expertiseLevel === 'expert'
              ? 'Suggested: H1 (44pt), H2 (24pt), Body (16pt)'
              : 'Try: 44pt for titles, 24pt for subtitles, 16pt for normal text',
            color: 'blue'
          });
        }
      }
    }
  }

  // Analyze layout issues
  const layoutScore = designQuality.scores?.layout_structure || 0;
  if (layoutScore < 50) {
    suggestions.push({
      type: 'layout',
      icon: Lightbulb,
      title: expertiseLevel === 'expert' ? 'Improve Margins & Spacing' : 'Add More Space Around Edges',
      description: expertiseLevel === 'expert'
        ? 'Define consistent margins. Content appears too close to slide edges.'
        : 'Move everything away from the edges of the slide to look more professional.',
      actionable: expertiseLevel === 'expert'
        ? 'Set margins: 0.5" (1.27cm) minimum on all sides'
        : 'Leave at least half an inch of empty space around all edges',
      color: 'purple'
    });
  }

  // Analyze proximity/grouping issues
  const proximityScore = designQuality.scores?.proximity_grouping || 0;
  if (proximityScore < 50) {
    suggestions.push({
      type: 'proximity',
      icon: Lightbulb,
      title: expertiseLevel === 'expert' ? 'Group Related Elements' : 'Put Related Things Closer Together',
      description: expertiseLevel === 'expert'
        ? 'Use proximity to show relationships. Related items should be closer together.'
        : 'Things that go together should be close to each other, with space between different groups.',
      actionable: expertiseLevel === 'expert'
        ? 'Suggested spacing: 8-16pt between related items, 24-48pt between groups'
        : 'Use small gaps (10pt) within groups, big gaps (30pt) between groups',
      color: 'green'
    });
  }

  if (suggestions.length === 0) return null;

  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
  };

  const iconColorClasses = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    green: 'text-green-600 dark:text-green-400'
  };

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {expertiseLevel === 'expert' ? 'Actionable Improvements' : 'How to Fix This'}
      </h4>
      <div className="space-y-2">
        {suggestions.map((suggestion, index) => {
          const Icon = suggestion.icon;
          return (
            <div
              key={index}
              className={`border rounded-lg p-3 ${colorClasses[suggestion.color]}`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColorClasses[suggestion.color]}`} />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-semibold text-foreground">{suggestion.title}</p>
                  <p className="text-xs text-muted-foreground">{suggestion.description}</p>
                  <div className="bg-white/50 dark:bg-black/20 rounded px-3 py-2 border border-border/50">
                    <p className="text-xs font-mono text-foreground">
                      💡 {suggestion.actionable}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
